import { Button } from "@mui/material";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import * as XLSX from "xlsx";

const ExportCsvButtonTributario = (props) => {
  const {
    data = [],
    columns = [],
    filename = "export",
    cnpj = "",
    descricao = "",
    buttonLabel = "Exportar Excel",
    onSuccess,
    onError,
    ...buttonProps
  } = props;

  const exportToExcel = () => {
    try {
      if (!Array.isArray(data) || data.length === 0) {
        onError?.("Não há dados para exportar!");
        return;
      }

      if (!Array.isArray(columns) || columns.length === 0) {
        onError?.("Não há colunas definidas para exportar!");
        return;
      }

      // Preparar dados para Excel
      const excelData = data.map((row) => {
        const excelRow = {};
        columns.forEach((col) => {
          const value = row[col.key];
          let processedValue = value;

          // Debug: Log para investigar valores
          console.log(`Processing column ${col.key} (${col.tipo}):`, {
            value,
            hasRaw: !!row.__raw,
            rawValue: row.__raw
              ? row.__raw[`${col.key}__${col.tipo}`]
              : undefined,
            rowKeys: row.__raw ? Object.keys(row.__raw) : [],
            allRowData: row,
          });

          // Tratar diferentes tipos de dados
          if (value === null || value === undefined) {
            processedValue = "";
          } else if (typeof value === "object" && value !== null) {
            // Para dados do PIS/COFINS que têm __raw
            if (
              row.__raw &&
              row.__raw[`${col.key}__${col.tipo}`] !== undefined
            ) {
              const rawValue = row.__raw[`${col.key}__${col.tipo}`];
              if (col.tipo === "boolean") {
                processedValue = rawValue ? "Sim" : "Não";
              } else if (
                col.tipo === "monetary" ||
                col.tipo === "percent" ||
                col.tipo === "number"
              ) {
                // Converter para número e remover símbolos monetários
                let numericValue = rawValue;
                if (typeof rawValue === "string") {
                  // Remover símbolos monetários ($, R$, etc.) e caracteres não numéricos exceto vírgula e ponto
                  numericValue = rawValue.replace(/[^\d,.-]/g, "");
                  // Converter vírgula para ponto para parseFloat
                  numericValue = numericValue.replace(",", ".");
                }
                processedValue = Number(numericValue) || 0;
              } else {
                processedValue = String(rawValue || "");
              }
            } else {
              processedValue = String(value);
            }
          } else if (typeof value === "boolean") {
            processedValue = value ? "Sim" : "Não";
          } else if (typeof value === "number") {
            processedValue = value;
          } else if (typeof value === "string") {
            // Para strings que podem ser valores monetários formatados
            if (
              col.tipo === "monetary" ||
              col.tipo === "percent" ||
              col.tipo === "number"
            ) {
              // Remover símbolos monetários e converter para número
              let numericValue = value.replace(/[^\d,.-]/g, "");
              numericValue = numericValue.replace(",", ".");
              processedValue = Number(numericValue) || 0;

              // Debug específico para valores monetários
              console.log(
                `Monetary conversion: "${value}" -> "${numericValue}" -> ${processedValue}`
              );
            } else {
              processedValue = String(value);
            }
          } else {
            processedValue = String(value);
          }

          // FALLBACK: Se ainda não temos um valor válido, tentar acessar diretamente do __raw
          if ((processedValue === "" || processedValue === 0) && row.__raw) {
            const rawValue = row.__raw[`${col.key}__${col.tipo}`];
            if (rawValue !== undefined && rawValue !== null) {
              if (
                col.tipo === "monetary" ||
                col.tipo === "percent" ||
                col.tipo === "number"
              ) {
                let numericValue = rawValue;
                if (typeof rawValue === "string") {
                  numericValue = rawValue
                    .replace(/[^\d,.-]/g, "")
                    .replace(",", ".");
                }
                processedValue = Number(numericValue) || 0;

                // Debug específico para fallback monetário
                console.log(
                  `Fallback monetary: "${rawValue}" -> "${numericValue}" -> ${processedValue}`
                );
              } else {
                processedValue = String(rawValue);
              }
            }
          }

          excelRow[col.label || col.key] = processedValue;
        });
        return excelRow;
      });

      // Criar workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Aplicar formatação e alinhamento
      const range = XLSX.utils.decode_range(ws["!ref"]);

      // Definir largura das colunas
      const colWidths = [];

      columns.forEach((col, index) => {
        // Calcular largura da coluna baseada no conteúdo
        const maxLength = Math.max(
          (col.label || col.key).length,
          ...data.map((row) => {
            const value = row[col.key];
            return String(value || "").length;
          })
        );
        colWidths.push({ wch: Math.min(Math.max(maxLength, 10), 50) });
      });

      // Aplicar larguras das colunas
      ws["!cols"] = colWidths;

      // Aplicar alinhamento às células
      for (let row = range.s.r; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) continue;

          if (!ws[cellAddress].s) ws[cellAddress].s = {};

          // Determinar alinhamento baseado no tipo da coluna
          const columnType = columns[col]?.tipo;
          let cellAlignment = { h: "left" }; // Padrão

          if (
            columnType === "monetary" ||
            columnType === "percent" ||
            columnType === "number"
          ) {
            cellAlignment = { h: "right" };
          }

          ws[cellAddress].s.alignment = cellAlignment;
        }
      }

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(wb, ws, "Dados");

      // Gerar nome do arquivo
      let finalFilename = filename;

      // Se CNPJ e descrição foram fornecidos, usar formato: cnpj_descricao
      if (cnpj && descricao) {
        // Remover caracteres especiais do CNPJ e descrição
        const cnpjClean = cnpj.replace(/\D/g, ""); // Remove tudo que não é dígito
        const descricaoClean = descricao
          .replace(/[^a-zA-Z0-9]/g, "") // Remove caracteres especiais
          .toLowerCase();
        finalFilename = `${cnpjClean}_${descricaoClean}`;
      } else if (cnpj) {
        // Se só CNPJ foi fornecido
        const cnpjClean = cnpj.replace(/\D/g, "");
        finalFilename = `${cnpjClean}_${filename}`;
      } else if (descricao) {
        // Se só descrição foi fornecida
        const descricaoClean = descricao
          .replace(/[^a-zA-Z0-9]/g, "")
          .toLowerCase();
        finalFilename = `${descricaoClean}_${filename}`;
      }

      // Exportar arquivo Excel
      XLSX.writeFile(wb, `${finalFilename}.xlsx`);

      onSuccess?.("Arquivo Excel exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar Excel:", error);
      onError?.(
        "Ocorreu um erro ao gerar o arquivo Excel. Por favor, tente novamente."
      );
    }
  };

  return (
    <Button
      onClick={exportToExcel}
      startIcon={<FileDownloadRoundedIcon />}
      variant="outlined"
      color="primary"
      size="small"
      sx={{
        borderColor: "#18316e",
        color: "#18316e",
        fontSize: "0.8rem",
        minWidth: "140px",
        transition: "transform 0.3s ease-in-out",
        "&:hover": {
          transform: "scale(1.05)",
          border: "1px solid #18316e",
        },
      }}
      {...buttonProps}
    >
      {buttonLabel}
    </Button>
  );
};

export default ExportCsvButtonTributario;
